<template>
    <div class="dashboard-products-preparation h-100 text-center">
        <transition name="loading-screen">
            <LoadingScreen
                v-if="loading || loadingStats.countLoading"
                :text="loadingStats.countLoaded + ' / ' + loadingStats.countTotal"
            />
        </transition>
        <template v-if="errors">
            <ErrorsZone :errors="errors" />
        </template>
        <template v-if="!errors">
            <div class="d-flex flex-row dashboard-row">
                <div class="row no-gutters flex-fill">
                    <div class="col-3 p-3 border border-black text-white">
                        <div class="h1 mb-0">
                            Du {{ todayHumanDate }}<br />
                            au {{ endHumanDate }}
                        </div>
                    </div>
                    <div class="col-9 p-3 border border-black text-white">
                        <div class="h1 mb-0">
                            Préparation des produits<br />
                            Catégorie : {{category.name}}
                        </div>
                    </div>
                </div>
            </div>
            <alerts type="dashboards"></alerts>
            <template v-if="!loadedOnce">
                <div class="d-flex flex-row dashboard-row">
                    <div class="row no-gutters flex-fill">
                        <div class="col-12 p-3 border border-black text-white text-center preparation-bg-cyan">
                            <div class="h1 mb-1">
                                <i class="fas fa-circle-notch fa-spin"></i>
                                Chargement...<br />
                            </div>
                            <div class="h2 mb-0">
                                Chargement du jour {{ loadingStats.countLoaded }} / {{ loadingStats.countTotal }}
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="d-flex flex-row dashboard-row">
                    <div class="sidebars text-white border border-black flex-shrink-0">
                        <div class="rotate rotate-left h5">
                            Produits
                        </div>
                    </div>
                    <div class="flex-fill">
                        <template v-for="category in _.orderBy(productsByCategory, ['order'])">
                            <div class="row no-gutters">
                                <template v-for="product in _.orderBy(category['products'], ['sku'])">
                                    <div class="dashboard-order-box p-3 border border-dark col-2" :class="'preparation-bg-' + product.preparation_status.color">
                                        <div class="h2 mb-0 text-ellipsis">
                                            <small>
                                                <MissingProductsIcon v-if="product.preparation_status.preparation_counters.nb_missing > 0"></MissingProductsIcon>
                                            </small>
                                            {{ product.tiny_name }}
                                        </div>
                                        <div class="h1 mb-0">
                                            {{ product.preparation_status.numerator }}
                                            <template v-if="product.preparation_status.preparation_counters.nb_toremove > 0">
                                                + {{ product.preparation_status.preparation_counters.nb_toremove }}
                                            </template>
                                            / {{ product.preparation_status.denominator }}
                                        </div>
                                        <div v-if="screen.options.displayDates">
                                            <div v-for="(ordersIds, date) in _(product.orders.by_date).toPairs().orderBy([0]).fromPairs().value()">
                                                {{ dateStringToHuman(date) }}
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </template>
                    </div>
                    <div class="sidebars text-white border border-black flex-shrink-0">
                        <div class="rotate rotate-right">
                            Produits
                        </div>
                    </div>
                </div>
            </template>
        </template>
    </div>
</template>

<script>
    import DashboardOrderBox from "./DashboardOrderBox";
    import ErrorsZone from "../../ErrorsZone";
    import LoadingScreen from "../../LoadingScreen";
    import MissingProductsIcon from "../../Preparation/MissingProductsIcon"
    import {useGetLoader} from "../../compositions/axios/loadingRequest";
    import {momentDay} from "../../../moment-with-helpers";
    import {preparationBgClass} from "../../../preparation-classes";

    export default {
        name: "DashboardProductsPreparation",

        components: {
            DashboardOrderBox,
            ErrorsZone,
            LoadingScreen,
            MissingProductsIcon,
        },

        props: {
            screenUid: {
                type: String,
                required: true,
            },
            screen: {
                type: Object,
                required: false,
            },
        },

        setup() {
            const {
                loaded,
                loading,
                errors,
                load,
            } = useGetLoader();

            return {
                loaded,
                loading,
                errors,
                load,
            };
        },

        data() {
            return {
                loadedOnce: false,
                loadingDates: {},
                loadingStats: {
                    'countLoading': 0,
                    'countLoaded': 0,
                    'countTotal': 0,
                },
                productionSlipProducts: {},
                productsByCategory: {},
                productsByCategoryCache: {},
                category: {},
                data: {},
            };
        },

        created() {
            Echo.channel(`dashboards`).listen('OrderUpdatedEvent', (e) => {
                this.loadStateFromServer();
            });
        },

        async mounted() {
            await this.loadStateFromServer();
        },

        computed: {
            preparationBgClass,

            todayJsDate() {
                return momentDay()
            },
            todayHumanDate() {
                return this.todayJsDate.format('dddd Do MMMM')
            },
            endJsDate() {
                let duration = this.screen.options.duration.split(' ');
                return this.todayJsDate.clone().add(duration[0], duration[1])
            },
            endHumanDate() {
                return this.endJsDate.format('dddd Do MMMM')
            },
            categoryId() {
                let categoryId = 0;
                if(this.screen.options && this.screen.options.categoryId) {
                    categoryId = this.screen.options.categoryId;
                }
                return categoryId;
            },
        },

        methods: {
            dateStringToHuman: function(date) {
                return momentDay(date).format('dddd Do MMMM')
            },

            loadStateFromServer: async function () {
                this.loadingDates = {};
                this.updateLoadingProducts();
                let dates = this.enumerateDays();
                for (let key in dates) {
                    this.loadStateFromServerForSingleDate(dates[key], this.categoryId);
                }
            },

            loadStateFromServerForSingleDate: async function (date, categoryId) {
                this.loadingDates[date] = true;
                this.updateLoadingProducts();
                const data = await this.load(`/dashboards/preparation/${date}/categorie/${categoryId}/json`);
                if (data) {
                    this.category = data.category;
                    this.data[date] = data;
                    this.loadingDates[date] = false;
                    this.updateProducts();
                    this.updateLoadingProducts();
                }
            },

            updateProducts: function() {
                this.productsByCategoryCache = {};
                for(let date in this.data) {
                    if(this.data[date].products) {
                        for(let sku in this.data[date].products) {
                            let fk_category = this.data[date].products[sku]['fk_category'];
                            if(!this.productsByCategoryCache[fk_category]) {
                                this.productsByCategoryCache[fk_category] = {
                                    'products': {},
                                    'id': fk_category,
                                    'name': this.data[date].allCategories[fk_category]['name'],
                                    'order': this.data[date].allCategories[fk_category]['order'],
                                }
                            }
                            if(!this.productsByCategoryCache[fk_category].products[sku]) {
                                this.productsByCategoryCache[fk_category].products[sku] = {
                                    'id': this.data[date].products[sku]['id'],
                                    'sku': this.data[date].products[sku]['sku'],
                                    'tiny_name': this.data[date].products[sku]['tiny_name'],
                                    'orders': {
                                        'by_date': {},
                                        'all': {},
                                    },
                                    'preparation_status': {
                                        'color': 'gray',
                                        'numerator': 0,
                                        'denominator': 0,
                                        'preparation_counters': {
                                            'nb_ordered': 0,
                                            'nb_prepared': 0,
                                            'nb_missing': 0,
                                            'nb_accounted': 0,
                                            'nb_pending': 0,
                                            'nb_surplus': 0,
                                            'nb_toremove': 0,
                                        },
                                    },
                                };
                            }

                            this.productsByCategoryCache[fk_category].products[sku].preparation_status = this.mergePreparationStatuses(this.productsByCategoryCache[fk_category].products[sku].preparation_status, this.data[date].products[sku].preparation_status);

                            if(this.data[date].products[sku].orders) {
                                this.productsByCategoryCache[fk_category].products[sku].orders.by_date[date] = this.data[date].products[sku].orders;
                                for(let order_id in this.data[date].products[sku].orders) {
                                    this.productsByCategoryCache[fk_category].products[sku].orders.all[order_id] = this.data[date].products[sku].orders[order_id];
                                }
                            }
                        }
                    }
                }
            },

            mergePreparationColors(accumulatorColor, nextColor) {
                if (accumulatorColor === nextColor || accumulatorColor === 'gray') {
                    if (nextColor === 'yellow') {
                        return 'green';
                    }
                    return nextColor;
                }
                if (nextColor === 'gray') {
                    return accumulatorColor;
                }
                if (accumulatorColor === 'orange' || nextColor === 'orange' || accumulatorColor === 'red' || nextColor === 'red') {
                    return 'orange';
                }
                if (accumulatorColor === 'yellow' || nextColor === 'yellow' || accumulatorColor === 'green' || nextColor === 'green') {
                    return 'green';
                }
                // blue is supported by first condition (accumulatorColor === nextColor)
                return 'orange';
            },

            mergePreparationStatuses(status1, status2) {
                return {
                    color: this.mergePreparationColors(status1.color, status2.color),
                    numerator: status1.numerator + status2.numerator,
                    denominator: status1.denominator + status2.denominator,
                    preparation_counters: {
                        nb_ordered: status1.preparation_counters.nb_ordered + status2.preparation_counters.nb_ordered,
                        nb_offered: status1.preparation_counters.nb_offered + status2.preparation_counters.nb_offered,
                        nb_prepared: status1.preparation_counters.nb_prepared + status2.preparation_counters.nb_prepared,
                        nb_missing: status1.preparation_counters.nb_missing + status2.preparation_counters.nb_missing,
                        nb_accounted: status1.preparation_counters.nb_accounted + status2.preparation_counters.nb_accounted,
                        nb_pending: status1.preparation_counters.nb_pending + status2.preparation_counters.nb_pending,
                        nb_surplus: status1.preparation_counters.nb_surplus + status2.preparation_counters.nb_surplus,
                        nb_toremove: status1.preparation_counters.nb_toremove + status2.preparation_counters.nb_toremove,
                    }
                };
            },

            enumerateDays: function() {
                let now = this.todayJsDate.clone(), dates = [];
                while (now.isSameOrBefore(this.endJsDate)) {
                    dates.push(now.format('YYYY-MM-DD'));
                    now.add(1, 'days');
                }
                return dates;
            },

            updateLoadingProducts: function() {
                let countLoading = 0;
                let countLoaded = 0;
                let countTotal = 0;
                for (let key in this.loadingDates) {
                    if(this.loadingDates[key]) {
                        countLoading++;
                    } else {
                        countLoaded++;
                    }
                    countTotal++;
                }
                if(!countLoading && countTotal) {
                    this.loadedOnce = true;
                    this.productsByCategory = this.productsByCategoryCache;
                }
                this.loadingStats = {
                    'countLoading': countLoading,
                    'countLoaded': countLoaded,
                    'countTotal': countTotal,
                };
            },
        },
    }
</script>

<style lang="scss" scoped>
    .dashboard-products-preparation {
        background-color: #343a40;

        .dashboard-row {
            background-color: #343a40;
            border-bottom: 4px solid #000;
            min-height: 110px;
        }

        .sidebars {
            width: 70px;
            position: relative;
            line-height: 1;
            .rotate {
                font-size: 1.125rem;
                line-height: 1;
            }
        }

        .line-height-1 {
            line-height: 1;
        }

        .huge-number {
            font-size: 2.75rem;
        }
    }
</style>
